/**
 * @file Guarded standalone Compliance route startup and teardown.
 */

import {state} from "../../app/state/state.js";
import {hasComplianceAccess} from "../../app/routing/boardAccess.js";
import {
    renderMobileRouteHeaderActions,
} from "../../shared/ui/mobileRouteHeaderActions.js";
import {getComplianceBootstrap} from "./complianceApi.js";
import {
    openComplianceNotificationRecord,
    renderComplianceBoard,
} from "./complianceBoard.js";
import {deactivateComplianceModal} from "./complianceModals.js";
import {
    applyComplianceBootstrap,
    applyComplianceStartupError,
    beginComplianceRouteSession,
    getComplianceState,
    isComplianceGenerationCurrent,
    loadComplianceSummaryPage,
    resetComplianceRouteState,
} from "./complianceState.js";

let activeGeneration = null;

function renderStatus(generation = activeGeneration) {
    const board = document.getElementById("compliance-board");
    if (!board) {return;}
    const routeState = getComplianceState();

    if (routeState.loadState === "loading") {
        board.innerHTML = `
            <div class="rounded-lg border border-gray-200 bg-white p-6"
                role="status" aria-live="polite">
                <p class="text-sm text-gray-500">Loading Compliance...</p>
            </div>
        `;
        return;
    }
    if (routeState.loadState === "error") {
        board.innerHTML = `
            <div class="rounded-lg border border-red-200 bg-red-50 p-6" role="alert">
                <h3 class="font-semibold text-red-800">Compliance is unavailable</h3>
                <p class="mt-1 text-sm text-red-700">The Compliance register could not be loaded. Try again later.</p>
            </div>
        `;
        return;
    }
    if (!routeState.configured) {
        board.innerHTML = `
            <div class="rounded-lg border border-gray-200 bg-white p-6">
                <h3 class="font-semibold text-gray-800">Compliance is not configured yet</h3>
                <p class="mt-1 text-sm text-gray-500">An administrator must configure Compliance categories before tracked items can appear.</p>
            </div>
        `;
        return;
    }
    if (routeState.categories.length === 0) {
        board.innerHTML = `
            <div class="rounded-lg border border-gray-200 bg-white p-6">
                <h3 class="font-semibold text-gray-800">No Compliance categories available</h3>
                <p class="mt-1 text-sm text-gray-500">Your account is not an active member of a team assigned to an active Compliance category.</p>
            </div>
        `;
        return;
    }
    renderComplianceBoard(board, generation);
}

async function loadComplianceRoute(generation) {
    try {
        const bootstrap = await getComplianceBootstrap();
        if (!applyComplianceBootstrap(generation, bootstrap)) {return;}
        renderStatus(generation);
        if (bootstrap?.configured === true &&
            Array.isArray(bootstrap?.categories) &&
            bootstrap.categories.length > 0) {
            await loadComplianceSummaryPage(generation, "active");
        }
        if (isComplianceGenerationCurrent(generation)) {
            renderStatus(generation);
        }
    } catch (error) {
        if (!applyComplianceStartupError(generation, error)) {return;}
        console.error("Failed to load Compliance:", error);
        renderStatus(generation);
    }
}

/**
 * Renders the guarded Compliance route and starts bounded bootstrap/summary reads.
 * @returns {void}
 */
export function renderComplianceView() {
    const root = document.getElementById("compliance-view");
    const board = document.getElementById("compliance-board");
    if (!root || !board) {return;}

    if (!hasComplianceAccess(state.currentUserProfile)) {
        deactivateComplianceModal();
        resetComplianceRouteState();
        activeGeneration = null;
        board.innerHTML = `
            <div class="rounded-lg border border-red-200 bg-red-50 p-6" role="alert">
                <h3 class="font-semibold text-red-800">Compliance access denied</h3>
                <p class="mt-1 text-sm text-red-700">Your account does not have Compliance permission.</p>
            </div>
        `;
        return;
    }

    const mobileActions =
        root.querySelector("[data-mobile-route-header-actions-slot]");
    if (mobileActions) {
        mobileActions.innerHTML = renderMobileRouteHeaderActions();
    }

    const routeState = getComplianceState();
    if (activeGeneration === routeState.generation &&
        activeGeneration !== null) {
        renderStatus(activeGeneration);
        return;
    }

    activeGeneration = beginComplianceRouteSession();
    renderStatus(activeGeneration);
    void loadComplianceRoute(activeGeneration);
}

/**
 * Invalidates pending reads, closes Compliance-owned modal state, and releases
 * the active route marker.
 * @returns {void}
 */
export function deactivateComplianceView() {
    deactivateComplianceModal();
    resetComplianceRouteState();
    activeGeneration = null;
}

/**
 * Waits for the routed Compliance bootstrap, then opens the authorised target.
 * @param {object} target Authoritative callable response.
 * @returns {Promise<object>} Open outcome.
 */
export async function openComplianceNotificationRecordTarget(target) {
    const generation = activeGeneration;
    for (let attempt = 0; attempt < 100; attempt += 1) {
        if (activeGeneration !== generation ||
            state.currentView !== "compliance") {
            return {status: "navigation_not_completed"};
        }
        if (getComplianceState().loadState === "ready") {
            return openComplianceNotificationRecord({
                item: target.item,
                requirement: target.requirement,
                generation,
            });
        }
        await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return {status: "route_unavailable"};
}
